import React, { useState } from "react";

import { isEmpty } from "lodash/fp";

import Table from "@app/analytics/components/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableHead from "@app/analytics/components/Table/TableHead";
import useEventGroups from "@app/analytics/hooks/calendar/useEventGroups";
import * as Events from "@app/analytics/tracking/events";
import {
  PatchUserEventGroupAction,
  UserEventGroup,
} from "@app/analytics/types/eventGroupTypes";
import { TOOLTIPS } from "@app/common/content";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import type { Pagination } from "@app/common/hooks/usePagination";
import { toast } from "@app/common/hooks/useToasts";

import Stack from "@rui/Stack";

import EditRecurringMeetingModal from "./EditRecurringMeetingModal";
import UpcomingEventsTableEmptyState from "./UpcomingEventsTableEmptyState";
import UpcomingEventsTableRow from "./UpcomingEventsTableRow";
import * as UpcomingEventUtils from "./utils";
import { PREDICTED_ROLES } from "../constants";

const PRE_MEETING_SHARING_HEAD_CELLS: HeadCell<UserEventGroup>[] = [
  {
    id: "title",
    label: "Meeting",
  },
  {
    id: "date",
    label: "Date & Time",
    sortBy: (event) => event.startTime.toMillis(),
  },
  {
    id: "flexibleMeeting",
    label: "Flexible?",
    align: "center",
    sortable: true,
    sortBy: (event) => event.isFlexible,
    tooltip: TOOLTIPS.calendar.flexibleMeeting,
    withTooltipIcon: true,
  },
  {
    id: "predictedAccessLevel",
    label: "Predicted Role",
    sortBy: (event) =>
      event.predictedAccessLevel
        ? PREDICTED_ROLES[event.predictedAccessLevel].sortValue
        : 100,
    tooltip: TOOLTIPS.calendar.predictedRole,
    withTooltipIcon: true,
  },
  {
    id: "isReadInvited",
    label: "Add Read?",
    align: "center",
    tooltip: TOOLTIPS.calendar.inviteRead,
    withTooltipIcon: true,
    sortBy: (event) => UpcomingEventUtils.isReadInvited(event),
  },
  {
    id: "actions",
    label: "",
    align: "right",
    sortable: false,
  },
];

const HEAD_CELLS = PRE_MEETING_SHARING_HEAD_CELLS;

const UpcomingEventsTable = ({
  events,
  pagination,
}: {
  events: UserEventGroup[];
  pagination: Pagination;
}) => {
  const { isLoading, updateEvent } = useEventGroups();

  const [showApplyToAllModal, setShowApplyToAllModal] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<UserEventGroup | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    action: PatchUserEventGroupAction;
    value: boolean;
  } | null>(null);

  const { handleSort, order, orderBy, sortedData } =
    useDataTable<UserEventGroup>(events, HEAD_CELLS, {
      defaultOrderBy: "date",
      defaultOrder: "asc",
    });

  const handleRecurringEventEdit = (
    action: PatchUserEventGroupAction,
    event: UserEventGroup,
    toggleCheckedOn: boolean
  ) => {
    setEventToEdit(event);
    setConfirmAction({ action, value: toggleCheckedOn });
    return setShowApplyToAllModal(true);
  };

  const handleCloseModal = () => {
    setEventToEdit(null);
    setShowApplyToAllModal(false);
  };

  const handleToggleEventAction = (
    action: PatchUserEventGroupAction,
    event: UserEventGroup,
    toggleCheckedOn: boolean
  ) => {
    if (action === "optIn") {
      Events.inviteRead({
        inviteNumAttendees: event.numAttendees,
        inviteMethod: "direct",
        inviteAction: toggleCheckedOn ? "add" : "remove",
      });
    }

    updateEvent.mutate(
      {
        id: event.id,
        body: {
          [action]: toggleCheckedOn,
        },
      },
      {
        onError: () => {
          toast.error(
            "There was an error updating the event. Please refresh the page or try again later."
          );
        },
      }
    );
  };

  const handleConfirm = (selected: string) => {
    if (!eventToEdit || !confirmAction) return;

    if (confirmAction.action === "optIn") {
      Events.inviteRead({
        inviteNumAttendees: eventToEdit.numAttendees,
        inviteMethod: "direct",
        inviteAction: confirmAction.value ? "add" : "remove",
      });
    }

    updateEvent.mutate(
      {
        id: eventToEdit.id,
        body: {
          [confirmAction.action]: confirmAction.value,
          applyToRecurringEvent: selected === "allMeetingsInSeries",
        },
      },
      {
        onError: () => {
          toast.error(
            "There was an error applying your changes. Please refresh the page or try again later."
          );
        },
      }
    );

    handleCloseModal();
  };

  const handleToggleInvite =
    (calendarEvent: UserEventGroup) =>
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const callback = calendarEvent.recurringEventId
        ? handleRecurringEventEdit
        : handleToggleEventAction;
      callback("optIn", calendarEvent, checked);
    };

  const handleToggleFlexible =
    (calendarEvent: UserEventGroup) =>
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const callback = calendarEvent.recurringEventId
        ? handleRecurringEventEdit
        : handleToggleEventAction;
      callback("isFlexible", calendarEvent, checked);
    };

  const eventsForPage = sortedData.slice(
    pagination.pageSize * pagination.currentPage,
    pagination.pageSize * (pagination.currentPage + 1)
  );

  return (
    <>
      <Stack sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table>
          <TableHead
            headCells={HEAD_CELLS}
            onSort={handleSort}
            order={order}
            orderBy={orderBy}
            sx={{ display: { xxs: "none", sm: "table-header-group" } }}
          />
          <TableBody loading={isLoading}>
            {isEmpty(eventsForPage) ? (
              <UpcomingEventsTableEmptyState />
            ) : (
              eventsForPage.map((event) => (
                <UpcomingEventsTableRow
                  key={event.id}
                  event={event}
                  onToggleInvite={handleToggleInvite(event)}
                  onToggleFlexible={handleToggleFlexible(event)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Stack>

      <EditRecurringMeetingModal
        open={showApplyToAllModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default UpcomingEventsTable;
